import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  adoptManageArticles,
  createManageArticle,
  deleteManageArticle,
  listManageArticles,
  reorderManageArticles,
  updateManageArticle,
  type ArticleRow,
  type ManageArticleInput,
} from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { ManageClubNav, moveId, useManagedClub } from "../components/ManageClubNav";
import { RichTextEditor } from "../components/RichTextEditor";
import { PencilFillIcon, Trash3FillIcon } from "../components/icons";
import Toast from "../components/Toast";

const EMPTY: ManageArticleInput = { title: "", summary: "", body: "", status: "draft", publishDate: "" };

/** /manage/:clubId/news -- the club's news articles, in the order they
 *  show on the club's News page. Up/down sets that order. */
export default function ManageNewsPage() {
  const { clubId = "" } = useParams();
  const { user } = useAuth();
  const { club, error: clubError } = useManagedClub(clubId);
  const [articles, setArticles] = useState<ArticleRow[] | null>(null);
  const [form, setForm] = useState<ManageArticleInput>(EMPTY);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function refresh() {
    listManageArticles(clubId)
      .then((res) => setArticles(res.articles))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load news."));
  }
  useEffect(refresh, [clubId]);

  async function run(action: () => Promise<string | void>) {
    setError(null);
    setBusy(true);
    try {
      const msg = await action();
      if (msg) setInfo(msg);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  }

  function startEdit(a: ArticleRow) {
    setEditingId(a.articleId);
    setForm({ title: a.title, summary: a.summary ?? "", body: a.body, status: a.status, publishDate: a.publishDate });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function resetForm() {
    setEditingId(null);
    setForm(EMPTY);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    run(async () => {
      if (editingId) await updateManageArticle(clubId, editingId, form);
      else await createManageArticle(clubId, form);
      const title = form.title;
      resetForm();
      refresh();
      return `Saved "${title}".`;
    });
  }

  function move(id: string, delta: -1 | 1) {
    if (!articles) return;
    const ids = moveId(articles.map((a) => a.articleId), id, delta);
    run(async () => setArticles((await reorderManageArticles(clubId, ids)).articles));
  }

  if (clubError) return <p className="err">{clubError}</p>;
  if (!club) return <p>Loading…</p>;

  return (
    <div>
      <ManageClubNav club={club} />
      <h1>{club.name} news</h1>
      {error && <p className="err">{error}</p>}
      {info && <Toast message={info} onDismiss={() => setInfo(null)} />}

      <form onSubmit={handleSubmit} className="card">
        <h3>{editingId ? "Edit article" : "New article"}</h3>
        <label htmlFor="news-title">Title</label>
        <input id="news-title" value={form.title ?? ""} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
        <div style={{ marginTop: "0.75rem" }}>
          <label htmlFor="news-summary">Summary (shown on the news list)</label>
          <input id="news-summary" value={form.summary ?? ""} onChange={(e) => setForm({ ...form, summary: e.target.value })} />
        </div>
        <div style={{ marginTop: "0.75rem" }}>
          <label>Body</label>
          <RichTextEditor
            key={editingId ?? "new"}
            initialValue={form.body ?? ""}
            onChange={(html) => setForm((f) => ({ ...f, body: html }))}
          />
        </div>
        <div className="row" style={{ marginTop: "0.75rem" }}>
          <div>
            <label htmlFor="news-status">Status</label>
            <select
              id="news-status"
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value as ManageArticleInput["status"] })}
            >
              <option value="draft">Draft (hidden)</option>
              <option value="published">Published</option>
            </select>
          </div>
          <div>
            <label htmlFor="news-date">Date shown</label>
            <input id="news-date" type="date" value={form.publishDate ?? ""} onChange={(e) => setForm({ ...form, publishDate: e.target.value })} />
          </div>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem" }}>
          <button type="submit" disabled={busy}>{editingId ? "Save changes" : "Add article"}</button>
          {editingId && <button type="button" className="secondary" onClick={resetForm}>Cancel</button>}
        </div>
      </form>

      <h2>Page order</h2>
      <p className="muted">Articles show on {club.name}'s News page in this order. Drafts stay hidden.</p>
      {articles && articles.length === 0 && <p className="muted">No articles yet.</p>}
      {articles && articles.length > 0 && (
        <div className="table-scroll"><table>
          <thead>
            <tr><th>Order</th><th>Title</th><th>Date</th><th>Status</th><th /></tr>
          </thead>
          <tbody>
            {articles.map((a, i) => (
              <tr key={a.articleId}>
                <td>
                  <span className="order-buttons">
                    <button type="button" className="secondary" disabled={busy || i === 0} onClick={() => move(a.articleId, -1)} aria-label={`Move ${a.title} up`}>↑</button>
                    <button type="button" className="secondary" disabled={busy || i === articles.length - 1} onClick={() => move(a.articleId, 1)} aria-label={`Move ${a.title} down`}>↓</button>
                  </span>
                </td>
                <td>{a.title}</td>
                <td>{a.publishDate}</td>
                <td><span className={`pill ${a.status === "published" ? "success" : "warn"}`}>{a.status}</span></td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <button type="button" className="link" title="Edit" aria-label={`Edit ${a.title}`} onClick={() => startEdit(a)}>
                    <PencilFillIcon />
                  </button>
                  <button
                    type="button"
                    className="link"
                    title="Delete"
                    aria-label={`Delete ${a.title}`}
                    onClick={() =>
                      confirm(`Delete "${a.title}"?`) &&
                      run(async () => {
                        await deleteManageArticle(clubId, a.articleId);
                        if (editingId === a.articleId) resetForm();
                        refresh();
                        return `Deleted "${a.title}".`;
                      })
                    }
                  >
                    <Trash3FillIcon />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}

      {user?.isAdmin && (
        <p className="muted" style={{ marginTop: "1.5rem" }}>
          Site admin:{" "}
          <button
            type="button"
            className="secondary"
            disabled={busy}
            onClick={() =>
              confirm(`Move every site-wide article (from Admin) into ${club.name}? They'll stop showing on the service site.`) &&
              run(async () => {
                const res = await adoptManageArticles(clubId);
                setArticles(res.articles);
                return `Moved ${res.adopted} article${res.adopted === 1 ? "" : "s"} into ${club.name}.`;
              })
            }
          >
            Adopt site-wide news
          </button>
        </p>
      )}
    </div>
  );
}
